
/* eslint-disable vue/no-mutating-props */
// `target` is the orchestrator's shared form-model object (state.client),
// intentionally passed by reference so the step can correct fields in place.
// #679 Phase 2, mockup panels G/G2: a confirmation, not a form — the default
// action is one click. Corrections persist at book time via the
// ExistingClientId shape (StepByStepV2.book → /book/ corrections whitelist).
import AddressFields from '../parts/AddressFields.vue'
import Location from '~/components/Location.vue'

export default {
    components: { AddressFields, Location },
    props: {
        target: { type: Object, required: true },
        gate: { type: Boolean, default: false },
    },
    data () {
        return {
            editing: { name: false, phone: false, address: false },
        }
    },
    computed: {
        fullName () {
            return `${this.target.first_name || ''} ${this.target.last_name || ''}`.trim()
        },
        nameComplete () {
            return !!((this.target.first_name || '').trim() && (this.target.last_name || '').trim())
        },
        // The phone input stays open (not a toggle) while the account has no
        // number at all — it is required to continue, same rule the
        // eligibility step applied before this step took the job over (#568).
        phoneOpen () {
            return this.editing.phone || (!this.target.phone && !this.target.hasPhoneOnFile)
        },
        phoneDisplay () {
            return this.target.phone || 'On file'
        },
        // G2: a file with no street opens the address inputs outright — the
        // client only types the part we genuinely do not have.
        addressOpen () {
            return this.editing.address || !this.target.has_full_address
        },
        cityStateZip () {
            const cs = [this.target.city, this.target.state].filter(Boolean).join(', ')
            return [cs, this.target.zip_code].filter(Boolean).join(' ')
        },
        dobDisplay () {
            // The dobM/D/Y triple only exists when the visitor typed it into
            // the returning lookup. A logged-in client arrives via the preset
            // with `dob` as an ISO string instead (Aziz QA 2026-09-01: the
            // row read "00 / 00 /" for magic-link sessions) — fall back to it.
            let { dobM, dobD, dobY } = this.target
            if (!(dobM && dobD && dobY) && this.target.dob) {
                const [y, m, d] = String(this.target.dob).split('T')[0].split('-')
                dobY = dobY || y
                dobM = dobM || m
                dobD = dobD || d
            }
            const p = v => String(v || '').padStart(2, '0')
            return `${p(dobM)} / ${p(dobD)} / ${dobY || ''}`
        },
        allGood () {
            if (this.editing.name || this.editing.phone || this.editing.address) return false
            if (!this.target.phone && !this.target.hasPhoneOnFile) return false
            if (this.target.addressEchoed && !this.target.has_full_address) return false
            return true
        },
        // Location speaks { zip_code, fips, state }; mirror the target's
        // fields into that shape so it can preselect the right county.
        locationValue () {
            return {
                zip_code: this.target.zip_code || '',
                fips: this.target.fips || '',
                state: this.target.state || '',
            }
        },
    },
    methods: {
        startEdit (key) {
            this.$set(this.editing, key, true)
            this.$emit('change')
        },
        doneEdit (key) {
            this.$set(this.editing, key, false)
        },
        // Same masking AboutDemographicBlock applies — (xxx) xxx-xxxx.
        formatPhone (e) {
            const d = String(e.target.value || '').replace(/\D/g, '').slice(0, 10)
            let v = d
            if (d.length >= 4 && d.length < 7) v = `(${d.slice(0, 3)}) ${d.slice(3)}`
            else if (d.length >= 7) v = `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`
            this.target.phone = v
        },
        // Location handles the ZIP→county lookup; mirror it back onto the
        // target. Same source-of-truth rule as AboutDemographicBlock: the
        // explicit state <select> wins once set — only backfill while empty.
        onLocation (loc) {
            this.target.zip_code = (loc && loc.zip_code) || ''
            this.target.fips = (loc && loc.fips) || ''
            if (loc && loc.state && !this.target.state) this.$set(this.target, 'state', loc.state)
        },
        // #689: same rule as AboutDemographicBlock — the pick's ZIP goes through
        // Location so county/fips derive as when typed.
        onAddressZip (zip) {
            this.target.zip_code = zip
            this.target.fips = ''
        },
    },
}
